import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { withNamespaces } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import { withApollo } from 'react-apollo';
import classnames from 'classnames';
import moment from 'moment';
import currency from 'currency.js';
import Button from '@material-ui/core/Button';
import InfoIcon from '@material-ui/icons/Info';

import _last from 'lodash/last';
import _uniq from 'lodash/uniq';
import _compact from 'lodash/compact';
import _isEmpty from 'lodash/isEmpty';
import _find from 'lodash/find';
import _get from 'lodash/get';
import _orderBy from 'lodash/orderBy';
import _findIndex from 'lodash/findIndex';

import sleep from '../../../utils/sleep';
import combineStyles from '../../../utils/combineStyles';
import TypographyStyles from '../../../styles/typography';
import LayoutStyles from '../../../styles/layout';
import ButtonStyles from '../../../styles/button';
import RatesStyles from './Rates.styles';

import * as Queries from '../../../graphQL/queries/shipmentQueries';
import { all as allCarrierQuery } from '../../../graphQL/queries/carrierRelationship';

import {
  sanitizeShipment,
  removeUntouchedFields,
  pickRequiredToRateFields
} from '../../../utils/Shipment';
import { NewRate } from './RatesMock';
import { formatPrice } from '../../../utils/Shipment';

import TableRates from './TableRates';
import AddCustomRateModal from '../Modals/AddCustomRate';
import RatesRefreshState from './RatesRefreshState';
import RatesEmptyState from './RatesEmptyState';
import { FORMACTIONS, CURRENCY_SYMBOL } from '../../../constants/constants';
import { validateShipment } from './../../../utils/Validation';
import ErrorValidationModal from '../../../components/shipment/Modals/ErrorValidation';
import { AppContext } from '../../../AppProvider';
import { midnightSecondsToHour } from '../../../utils/DateTime';

import {
  calcTotalWeight,
  calcTotalPallets
} from '../../../components/shipment/form/ShipmentContents/helpers';

const UNSPECIFIED_LANE = 'Unspecified';

export class Rates extends Component {
  static propTypes = {
    values: PropTypes.object.isRequired,
    handleChange: PropTypes.func.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    touched: PropTypes.object,
    setErrors: PropTypes.func.isRequired,
    shipmentRates: PropTypes.array,
    selectedShipmentRate: PropTypes.object,
    isQuickRate: PropTypes.bool,
    fromQuickRate: PropTypes.bool,
    handleValidateField: PropTypes.func
  };
  constructor(props, context) {
    super(props, context);
    const {
      referenceData: { serviceLevels, laneTypes },
      orgInfo
    } = this.context.globalProps;

    this.state = {
      rows: [],
      expandedRowIds: [],
      isOpenAddCustomRateModal: false,
      customRate: { ...NewRate },
      isAddedCustomRate: false,
      lowestRate: null,
      selectedRateId: null,
      selectedRate: {},
      loadedRates: false,
      refreshingRates: false,
      serviceLevels,
      laneTypes,
      orgInfo,
      gettingRates: false,
      errorList: {},
      openValidationModal: false,
      currentPage: 0,
      pageSize: 10,
      availableCarriers: [],
      isVolumeQualified: false,
      volumeCustomRateModal: false,
      rateListGuid: this.props.values.rateListGuid
    };
  }

  async componentDidMount() {
    // Rates are going to be passed in when we are coming from a Quick Rate
    // or when we are editing an existing Shipment that already has associated Rates.
    // When coming from a Quick Rate they are already formatted so no need to call _processRates.
    const {
      shipmentRates = [],
      selectedShipmentRate,
      fromQuickRate
    } = this.props;
    const rates = fromQuickRate
      ? [...shipmentRates]
      : this._processRates([...shipmentRates]);

    // need to check if there is a custom rate.  This is for the case where rates, including
    // a custom one were transferred over from quick rate.

    let currCustomRate = null;
    if (rates.length) {
      currCustomRate = _find(rates, { isCustomRate: true });
    }

    this.setState(
      {
        selectedRateId: selectedShipmentRate
          ? selectedShipmentRate.rateItemGuid ||
            selectedShipmentRate.customRateItemGuid
          : null,
        rows: rates,
        lowestRate: rates[0] || {},
        loadedRates: !!rates.length,
        isAddedCustomRate: !_isEmpty(currCustomRate),
        customRate: currCustomRate
      },
      () => {
        if (fromQuickRate && rates.length) {
          this._handleSelectRate(rates[0].rateItemGuid);
        }
      }
    );

    await this._getCarriers();
  }

  componentDidUpdate(prevProps, prevState) {
    const { shipmentRates = [], selectedShipmentRate } = this.props;
    const { selectedRateId } = this.state;

    if (shipmentRates !== prevProps.shipmentRates) {
      this.setState({
        rows: [...shipmentRates],
        selectedRateId: selectedShipmentRate
          ? selectedShipmentRate.rateItemGuid ||
            selectedShipmentRate.customRateItemGuid
          : null,
        loadedRates: !!shipmentRates.length,
        lowestRate: shipmentRates[0] || {}
      });
    }

    if (selectedRateId !== prevState.selectedRateId) {
      this.setState({ selectedRate: this._getSelectedRate(), selectedRateId });
    }
  }

  _getCarriers = async () => {
    try {
      const { client: ApolloClient } = this.props;

      const {
        orgInfo: { orgId }
      } = this.context.globalProps;

      const {
        data: {
          carrier: { all: availableCarriers }
        }
      } = await ApolloClient.query({
        query: allCarrierQuery,
        variables: { orgId }
      });
      this.setState({ availableCarriers });
    } catch (error) {
      console.error(error);
    }
  };

  _eligibleForVolume = () => {
    const { values } = this.props;
    const {
      minimumLinearFeetForVltl,
      minimumPalletCountForVltl,
      minimumWeightInPoundsForVltl
    } = this.context.globalProps.orgInfo.config;
    const linearFeet = parseInt(_get(values, 'totalLinearFeet', 0), 10) || 0;
    if (linearFeet >= minimumLinearFeetForVltl) {
      return true;
    }

    if (calcTotalWeight(values.handlingUnits) >= minimumWeightInPoundsForVltl) {
      return true;
    }

    if (calcTotalPallets(values.handlingUnits) >= minimumPalletCountForVltl) {
      return true;
    }

    return false;
  };

  _handleGetRates = async ({ refresh = false }) => {
    this.setState({ refreshingRates: refresh, gettingRates: !refresh });
    const { orgInfo } = this.state;
    const {
      values,
      client: ApolloClient,
      selectedShipmentRate,
      setFieldValue,
      touched,
      setErrors,
      isQuickRate
    } = this.props;
    setFieldValue('ratedShipmentState', pickRequiredToRateFields(values));
    const shipment = removeUntouchedFields(FORMACTIONS.RATE, values, touched); //removes untouched fields from consideration for validation
    const errors = await validateShipment(
      FORMACTIONS.RATE,
      shipment,
      touched,
      isQuickRate
    );
    const formattedShipment = sanitizeShipment(
      FORMACTIONS.RATE,
      shipment,
      orgInfo
    );

    if (_isEmpty(errors)) {
      try {
        const {
          data: {
            shipment: { rateShipment = [] }
          }
        } = await ApolloClient.query({
          query: Queries.rateShipment,
          variables: { rateRequest: formattedShipment }
        });
        const rates = this._processRates(rateShipment);

        if (selectedShipmentRate && selectedShipmentRate.customRateItemGuid) {
          ///lets see if the customRateItemGuid is in the list of refreshed rates.
          const customRateInList = rates.some(rate => {
            if (
              selectedShipmentRate.customRateItemGuid ===
              rate.customRateItemGuid
            ) {
              return true;
            }
            return false;
          });
          if (!customRateInList) {
            //so this happens in a new shipment when you create a custom rate and then refresh rates
            rates.unshift(this._formatNewRate(this.state.customRate));
          }
        }
        this.setState(
          {
            rows: [...rates],
            lowestRate: rates[0] || {},
            selectedRateId: selectedShipmentRate
              ? selectedShipmentRate.rateItemGuid ||
                selectedShipmentRate.customRateItemGuid
              : null,
            customRate: refresh
              ? {
                  ...NewRate
                }
              : this.state.customRate,
            isAddedCustomRate: refresh ? false : this.state.isAddedCustomRate,
            isVolumeQualified: this._eligibleForVolume()
          },
          () => {
            setFieldValue('shipmentRates', rates);
            if (rates.length > 0) {
              setFieldValue('rateListGuid', rates[0].rateListGuid);
            }

            // Rerating an existing Shipment can cause the Shipment to be updated
            if (rates.length) {
              setFieldValue('rowVersion', rates[0].shipmentRowVersion);
              // the first rate is the LCP - we want to select that one
              this._handleSelectRate(rates[0].rateItemGuid);
            }
          }
        );
      } catch (error) {
        this.setState({ lowestRate: {}, rows: [], selectedRateId: null });
        console.error(error);
      } finally {
        await sleep(500);
        this.setState({
          refreshingRates: false,
          loadedRates: true,
          gettingRates: false,
          expandedRowIds: [],
          currentPage: refresh ? 0 : this.state.currentPage
        });
      }
    } else {
      setErrors(errors);
      this.setState({
        errorList: errors,
        openValidationModal: true,
        refreshingRates: false,
        loadedRates: false,
        gettingRates: false
      });
    }
  };

  _processRates = rates => {
    if (!_isEmpty(rates)) {
      return this._sortRates(rates).map(
        //once ordered the list the first element is the lowest
        (rate, index) => ({ ...this._formatRate(rate), isLowest: index === 0 })
      );
    }

    return [];
  };

  _sortRates = rates => {
    if (!_isEmpty(rates)) {
      return _orderBy(
        _uniq(_compact([...rates])),
        ['canBeSelected', 'carrierGrandTotal'],
        ['desc', 'asc']
      );
    }
    return [];
  };

  _getLevel = serviceLevelId => {
    const serviceLevel = this.state.serviceLevels.find(
      serviceLevel => serviceLevel.value === serviceLevelId
    );
    if (!serviceLevel) {
      return null;
    }
    return serviceLevel.title;
  };

  _handleOpenAddCustomRateModal = () => {
    this.setState({
      isOpenAddCustomRateModal: true,
      volumeRateCarrier: ''
    });
  };

  _handleOpenAddCustomVolumeRateModal = () => {
    const { customRate, selectedRate } = this.state;

    const newRate = { ...customRate };
    if (!_isEmpty(selectedRate)) {
      newRate.carrier = selectedRate.carrier;
    }

    this.setState({
      isOpenAddCustomRateModal: true,
      volumeCustomRateModal: true,
      customRate: { ...newRate }
    });
  };

  _handleCloseAddCustomRateModal = () => {
    this.setState({
      isOpenAddCustomRateModal: false,
      volumeCustomRateModal: false
    });
  };

  _formatRate = rate => {
    const {
      config: { defaultCurrency }
    } = this.state.orgInfo;
    const { laneTypes } = this.state;
    const { t } = this.props;
    const { openToTimeLocal } = this.props.values.pickup;
    const currencySymbol = CURRENCY_SYMBOL[defaultCurrency];
    let shipToday = '--';
    if (rate.canBeSelected) {
      shipToday =
        new Date(rate.latestDispatchTimeUtc) > new Date() ? t('Yes') : t('No');
    }
    let route = '--';
    if (rate.canBeSelected) {
      const routeType = (_find(
        laneTypes,
        lane => lane.enumValue === rate.laneTypeId
      ) || {name: ''}).name;
        
      route = routeType !== UNSPECIFIED_LANE ? routeType : t('N/A');
    }
    let prettyEstimatedDelivery = '--';
    if (rate.deliveryDateTimeLocal) {
      // they gave it to us, just use it
      prettyEstimatedDelivery = moment(rate.deliveryDateTimeLocal).format(
        'MMM DD, YYYY'
      );
    } else {
      // don't have a delivery date - as long as we have transit days let's
      // figure it out
      if (rate.transitDays) {
        const today = new Date();
        prettyEstimatedDelivery = moment(
          today.setDate(
            today.getDate() + (rate.transitDays + (shipToday === 'No' ? 1 : 0))
          )
        ).format('MMM DD, YYYY');
      }
    }
    const formattedRate = {
      id: rate.id,
      carrier: _get(rate, 'carrier.name', '') || _get(rate, 'carrierName'),
      carrierId: _get(rate, 'carrier.id', ''),
      carrierSupportsAutoTender: _get(
        rate,
        'carrier.supportsAutoTender',
        false
      ),
      dispatchPhoneNumber: _get(rate, 'carrier.dispatchPhoneNumber'),
      leadTimeWindowMinutes: _get(rate, 'carrier.leadTimeWindowMinutes', ''),
      prettyTransitTime: rate.transitDays
        ? `${rate.transitDays} ${t('Day')}${rate.transitDays > 1 ? 's' : ''}`
        : '--',
      transit_time: rate.transitDays,
      // prettyEstimatedDelivery: rate.deliveryDateTimeLocal
      //   ? moment(rate.deliveryDateTimeLocal).format('MMM DD, YYYY')
      //   : null,
      prettyEstimatedDelivery,
      prettyRate: rate.carrierGrandTotal
        ? formatPrice(rate.carrierGrandTotal, currencySymbol)
        : rate.canBeSelected
        ? null
        : t('Error'),
      rate: rate.carrierGrandTotal,
      carrierGrandTotal: rate.carrierGrandTotal,
      estimated_delivery: rate.deliveryDateTimeLocal,
      accessorial: formatPrice(rate.carrierAccessorialTotal, currencySymbol),
      transit: formatPrice(rate.carrierLineHaulTotal, currencySymbol),
      fuel: formatPrice(rate.carrierFuelTotal, currencySymbol),
      rateListGuid: rate.rateListGuid,
      rateItemGuid: rate.rateItemGuid,
      customRateItemGuid: rate.customRateItemGuid,
      isCustomRate: rate.isCustomRate,
      prettyLevel: this._getLevel(rate.serviceLevelId),
      level: rate.serviceLevelId,
      quoteId: rate.carrierQuoteNumber,
      messages: rate.messages,
      canBeSelected: rate.canBeSelected,
      shipmentId: rate.shipmentId,
      shipmentRowVersion: rate.shipmentRowVersion,
      isVolumeRate: rate.isVolumeRate,
      type: rate.isVolumeRate ? 'V' : rate.isCustomRate ? 'C' : '',
      notes: rate.notes,
      shipToday,
      route,
      dockCloseTime: openToTimeLocal
        ? midnightSecondsToHour(openToTimeLocal)
        : null
    };
    if (rate.isCustomRate) {
      this.setState({ isAddedCustomRate: true, customRate: formattedRate });
    }
    return formattedRate;
  };

  //Start
  _formatNewRate = newRate => {
    const {
      config: { defaultCurrency }
    } = this.state.orgInfo;
    return {
      ...newRate,
      prettyEstimatedDelivery: newRate.estimated_delivery
        ? moment(newRate.estimated_delivery, 'YYYY-MM-DD').format(
            'MMM DD, YYYY'
          )
        : null,
      estimated_delivery: newRate.estimated_delivery,
      prettyRate: newRate.rate
        ? currency(newRate.rate, {
            symbol: CURRENCY_SYMBOL[defaultCurrency],
            formatWithSymbol: true
          }).format()
        : null,
      rate: newRate.rate,
      prettyTransitTime: newRate.transit_time
        ? `${newRate.transit_time} Days`
        : null,
      transit_time: newRate.transit_time,
      prettyLevel: newRate.level ? this._getLevel(newRate.level) : null,
      customRateItemGuid: newRate.customRateItemGuid,
      isVolumeRate: newRate.isVolumeRate,
      isCustomRate: newRate.isCustomRate,
      type: newRate.isVolumeRate ? 'VC' : 'C',
      notes: newRate.notes,
      canBeSelected: true
    };
  };
  //End

  _handleSaveCustomRate = newRate => {
    let { rows: currentRates } = this.state;
    const { pageSize, lowestRate } = this.state;

    const customRate = this._formatNewRate(newRate, true);
    if (this.state.isAddedCustomRate) {
      // this is the case when a user is updating an existing custom rate
      //first we need to remove the existing custom rate.
      const existingCustomRateIndex = currentRates.find(rate => {
        if (rate.isCustomRate) {
          return rate;
        }
        return false;
      });
      currentRates.splice(existingCustomRateIndex, 1);
    }
    currentRates = [customRate, ...currentRates];
    const customRatePosition = _findIndex(currentRates, customRate);

    // is this now the lowest rate?
    const isLowest =
      customRate.carrierGrandTotal < lowestRate.carrierGrandTotal;
    if (isLowest) {
      this.setState({ lowestRate: customRate });
    }

    this.setState(
      {
        rows: [...currentRates],
        customRate: {
          ...newRate
        },
        isAddedCustomRate: true,
        isOpenAddCustomRateModal: false,
        selectedRateId: newRate.customRateItemGuid
      },
      () => {
        this.setState({
          expandedRowIds: isLowest ? [] : [customRatePosition],
          currentPage: Math.floor(customRatePosition / pageSize)
        });
      }
    );
    this.props.setFieldValue('selectedShipmentRate', newRate);
    this.props.setFieldValue('shipmentRates', currentRates);
    this.props.setFieldValue('customShipmentRates', newRate);
    this.props.setFieldValue('customRateItemGuid', newRate.customRateItemGuid);
  };

  _getSelectedRate = () => {
    const { rows: currentRates, selectedRateId, lowestRate } = this.state;
    return selectedRateId
      ? currentRates.find(
          currentRate =>
            currentRate.rateItemGuid === selectedRateId ||
            currentRate.customRateItemGuid === selectedRateId
        )
      : lowestRate;
  };

  _handleSelectRate = rateId => {
    const { handleChange, isQuickRate, handleSelectRate } = this.props;
    const { rows, pageSize } = this.state;

    // find the selected one to make sure it is actually selectable, if not ignore the select
    const rate = _find(
      rows,
      row => row.customRateItemGuid === rateId || row.rateItemGuid === rateId
    );

    if (!rate.canBeSelected || isQuickRate) {
      return;
    }

    const rateIndex = _findIndex(rows, rate);
    this.setState(
      {
        selectedRateId: rateId,
        currentPage: Math.floor(_findIndex(rows, rate) / pageSize),
        expandedRowIds:
          rateId === this.state.lowestRate.rateItemGuid ||
          rateId === this.state.lowestRate.customRateItemGuid
            ? []
            : [rateIndex]
      },
      () => {
        const selectedRate = this._getSelectedRate();
        if (handleSelectRate) {
          handleSelectRate(selectedRate);
        }
        handleChange({
          target: { name: 'rateListGuid', value: selectedRate.rateListGuid }
        });
        handleChange({
          target: { name: 'rateItemGuid', value: selectedRate.rateItemGuid }
        });
        handleChange({
          target: { name: 'selectedShipmentRate', value: selectedRate }
        });
        handleChange({
          target: {
            name: 'customRateItemGuid',
            value: selectedRate.customRateItemGuid
          }
        });
      }
    );
  };

  _changeExpandedDetails = (
    expandedRowIds,
    { isLowest, customRateItemGuid, rateItemGuid }
  ) => {
    const selectedRateId = rateItemGuid || customRateItemGuid;
    const { rows } = this.state;
    // Figure out the expanded row IDs (selected rates that are selectable and aren't the LCP).
    // - If they selected the lowest rate we want none expanded.
    // - If they clicked on a non-selectable row, ignore it (leave it as it was)
    // - Otherwise use the one they clicked
    let rowId = this.state.expandedRowIds;
    if (isLowest) {
      rowId = [];
    } else {
      // find the rate to see if it is selectable
      const rate = _find(
        rows,
        row =>
          row.customRateItemGuid === selectedRateId ||
          row.rateItemGuid === selectedRateId
      );
      if (rate.canBeSelected) {
        rowId = [_last(expandedRowIds)];
      }
    }

    this.setState(
      {
        expandedRowIds: rowId
      },
      () => {
        this._handleSelectRate(selectedRateId);
      }
    );
  };

  _handleCloseErrorValidation = () => {
    this.setState({ openValidationModal: false });
  };

  _handleChangeCurrentPage = currentPage => this.setState({ currentPage });

  _handleChangePageSize = pageSize => this.setState({ pageSize });

  _getCarrierInfo = () => {
    const { selectedRate } = this.state;
    const { t } = this.props;
    if (!selectedRate || (selectedRate && !selectedRate.dispatchPhoneNumber)) {
      return t(
        `Please contact ${
          selectedRate.carrier ? selectedRate.carrier : 'your carrier'
        } for a volume rate.`
      );
    }
    return `Please contact ${selectedRate.carrier} at ${
      selectedRate.dispatchPhoneNumber
    } for a volume rate.`;
  };

  _shouldShowSelectThisRate = () => {
    const { selectedRateId, lowestRate } = this.state;
    return (
      !selectedRateId ||
      (selectedRateId !== lowestRate.id &&
        selectedRateId !== lowestRate.rateItemGuid &&
        selectedRateId !== lowestRate.customRateItemGuid)
    );
  };

  render() {
    const {
      classes,
      t,
      values,
      isQuickRate = false,
      handleValidateField
    } = this.props;
    const {
      rows: currentRates,
      expandedRowIds,
      isOpenAddCustomRateModal,
      isAddedCustomRate,
      customRate,
      selectedRateId,
      loadedRates,
      refreshingRates,
      gettingRates,
      errorList,
      openValidationModal,
      lowestRate,
      currentPage,
      pageSize,
      volumeCustomRateModal,
      isVolumeQualified
    } = this.state;

    const showSelectRate = this._shouldShowSelectThisRate();

    return (
      <Fragment>
        <div className={classes.root}>
          {!loadedRates && (
            <RatesEmptyState
              classes={classes}
              onClick={this._handleGetRates}
              isSubmitting={gettingRates}
            />
          )}
          {refreshingRates && <RatesRefreshState classes={classes} />}
          {loadedRates && (
            <Fragment>
              <div
                className={classnames(
                  classes.titleRootWrapper,
                  classes.titleContainer
                )}
              >
                <h2
                  className={classnames(classes.headline7, classes.mainTitle)}
                >
                  {t('Rates')}
                </h2>
                {!isQuickRate && (
                  <Button
                    color="primary"
                    className={classnames(
                      classes.linkButton,
                      classes.rateActionLink,
                      classes.customRateLinkAction
                    )}
                    classes={{
                      label: classnames(
                        classes.body2,
                        classes.rateActionLinkLabel
                      )
                    }}
                    onClick={this._handleOpenAddCustomRateModal}
                  >
                    {isAddedCustomRate
                      ? t('Update Custom Rate')
                      : t('Add Custom Rate')}
                  </Button>
                )}
                <Button
                  onClick={() => this._handleGetRates({ refresh: true })}
                  color="primary"
                  className={classnames(
                    classes.linkButton,
                    classes.rateActionLink
                  )}
                  classes={{
                    label: classnames(
                      classes.body2,
                      classes.rateActionLinkLabel
                    )
                  }}
                >
                  {t('Refresh Rates')}
                </Button>
              </div>
              {!isQuickRate && (
                <div className={classes.selectedRate}>
                  <div className={classes.lowestCostMessageContainer}>
                    <p
                      className={classnames(
                        classes.body2,
                        classes.lowestCostMessage
                      )}
                    >
                      {t('LOWEST COST')}
                    </p>
                    <p
                      className={classnames(
                        classes.body2,
                        classes.lowestCostMessage
                      )}
                    >
                      {t('PROVIDER')}
                    </p>
                  </div>
                  <div className={classes.carrierInfoContainer}>
                    <p
                      className={classnames(
                        classes.headline6,
                        classes.carrierName
                      )}
                    >
                      {lowestRate.carrier}
                    </p>
                    <p
                      className={classnames(
                        classes.body2,
                        classes.deliveryInfo
                      )}
                    >
                      {`${lowestRate.prettyLevel} (${
                        lowestRate.prettyTransitTime
                      })`}
                    </p>
                    <p
                      className={classnames(
                        classes.body2,
                        classes.deliveryInfo,
                        classes.estimatedDeliveryInfo
                      )}
                    >
                      {`${t(
                        'Estimated Delivery'
                      )}: ${lowestRate.prettyEstimatedDelivery || '--'}`}
                    </p>
                  </div>
                  <div className={classes.priceDetailsContainer}>
                    {lowestRate.accessorial && (
                      <p
                        className={classnames(
                          classes.caption,
                          classes.priceDetails
                        )}
                      >
                        {`${t('Accessorial')}: ${lowestRate.accessorial}`}
                      </p>
                    )}
                    {lowestRate.transit && (
                      <p
                        className={classnames(
                          classes.caption,
                          classes.priceDetails
                        )}
                      >
                        {`${t('Transit')}: ${lowestRate.transit}`}
                      </p>
                    )}
                    {lowestRate.fuel && (
                      <p
                        className={classnames(
                          classes.caption,
                          classes.priceDetails
                        )}
                      >
                        {`${t('Fuel')}: ${lowestRate.fuel}`}
                      </p>
                    )}
                  </div>
                  <div className={classes.selectRateContainer}>
                    <p className={classnames(classes.headline5, classes.price)}>
                      {lowestRate.prettyRate}
                    </p>
                    {showSelectRate && (
                      <Button
                        variant="contained"
                        color="primary"
                        className={classnames(
                          classes.button,
                          classes.selectRateButton
                        )}
                        classes={{ label: classes.buttonLabel }}
                        onClick={
                          selectedRateId === lowestRate.id
                            ? null
                            : () =>
                                this._handleSelectRate(
                                  lowestRate.rateItemGuid ||
                                    lowestRate.customRateItemGuid
                                )
                        }
                      >
                        {t('Select This Rate')}
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {isVolumeQualified && !isQuickRate && (
                <div
                  className={classnames(classes.body1, classes.customRateDiv)}
                >
                  {!isAddedCustomRate && (
                    <Button
                      color="primary"
                      className={classnames(
                        classes.linkButton,
                        classes.rateActionLink,
                        classes.customRateLinkAction
                      )}
                      classes={{
                        label: classnames(
                          classes.body2,
                          classes.rateActionLinkLabel
                        )
                      }}
                      onClick={this._handleOpenAddCustomVolumeRateModal}
                    >
                      {isAddedCustomRate
                        ? t('Update Custom Rate')
                        : t('Add Custom Volume Rate')}
                    </Button>
                  )}
                  <span>
                    <InfoIcon color="inherit" className={classes.infoIcon} />
                  </span>
                  <span className={classes.volumeEligible}>
                    {t('This shipment may qualify for a volume rate')}.
                    <div className={classes.carrierInfo}>
                      {this._getCarrierInfo()}
                    </div>
                  </span>
                </div>
              )}

              <TableRates
                rows={currentRates}
                expandedRowIds={expandedRowIds}
                onExpandedRowIdsChange={this._changeExpandedDetails}
                selectedRateId={selectedRateId}
                values={values}
                isQuickRate={isQuickRate}
                currentPage={currentPage}
                pageSize={pageSize}
                handlePageChange={this._handleChangeCurrentPage}
                handlePageSizeChange={this._handleChangePageSize}
                handleValidateField={handleValidateField}
              />
            </Fragment>
          )}
        </div>
        {isOpenAddCustomRateModal && (
          <AddCustomRateModal
            onClose={this._handleCloseAddCustomRateModal}
            onSave={this._handleSaveCustomRate}
            rate={customRate}
            volume={volumeCustomRateModal}
            availableCarriers={this.state.availableCarriers}
          />
        )}
        <ErrorValidationModal
          open={openValidationModal}
          errors={errorList}
          onClose={this._handleCloseErrorValidation}
        />
      </Fragment>
    );
  }
}

Rates.contextType = AppContext;
const RatesWithTranslations = withNamespaces()(withApollo(Rates));
export default withStyles(
  combineStyles(RatesStyles, TypographyStyles, LayoutStyles, ButtonStyles)
)(RatesWithTranslations);
